import { DateService } from '@application/time/date.service';
import { Presentation, SynchronousPresentation, usePresentation } from '@visiba/presentation';
import { Disposer, Timer } from '@visiba/std';

export type CounterState = [state: SynchronousPresentation<Presentation>, disposer: Disposer];

// biome-ignore lint/suspicious/noRedeclare: suppressed due to migration - should be fixed
interface Presentation {
	time: number;
	hasElapse: boolean;
	hasBeenTriggeredExternally: boolean;
}

const INTERVAL = 100;

export function createCounterState(startTime: VisibaDate): CounterState {
	let prevMin = getElapsedTime(startTime)[0];
	// JavaScript is a single threaded language and we can't reliably count on the
	// event loop being accurate. To ensure steady interval executing the callback
	// without drift.
	let expected = Date.now() + INTERVAL;

	function getElapsedTime(time: VisibaDate): [minute: number, absolute: number] {
		const ms = time.diff(DateService.newDate(), 'milliseconds');

		return [Math.ceil(ms / 60_000), ms];
	}

	const tick = (): void => {
		const drift = Date.now() - expected;

		expected += INTERVAL;

		const [minute, absoluteMs] = getElapsedTime(startTime);

		if (absoluteMs <= 0) {
			timer.flush();

			presentation.write({
				hasElapse: true,
				time: 0,
			});

			return;
		}

		if (prevMin !== minute) {
			prevMin = minute;
			presentation.write({
				time: prevMin,
			});
		}

		timer.wait(tick, Math.max(0, INTERVAL - drift)); // Take into account drift
	};

	const timer = new Timer();
	const presentation = Presentation.create({
		time: prevMin,
		hasElapse: false,
		hasBeenTriggeredExternally: false,
	});

	timer.wait(tick, INTERVAL);

	return [presentation, timer.flush];
}

export function useCounterState(countDowner: CounterState): Presentation {
	return usePresentation(countDowner[0]);
}
